import { AssetDto, AssetsOwnershipPart } from '@/shared/lib/sj-orm/models/asset.dto';
import {
  BankCellDto,
  BondsDto,
  BrokerageAccountDto,
  CentralizedWalletDto,
  CommercialRealEstateDto,
  DebitAccountDto,
  DecentralizedWalletDto,
  ExchangeOptionsDto,
  ForwardsDto,
  FuturesDto,
  JewelryDto,
  LandDto,
  LegalEntityDto,
  MetalAccountDto,
  MyFundDto,
  OverTheCounterOptionsDto,
  PreciousGemstonesDto,
  PublicFundDto,
  ResidentialRealEstateDto,
  SecuritiesAndStocksDto,
} from '@/shared/lib/sj-orm/models/assets/categories';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';

import {
  isBankCell,
  isBond,
  isBrokerageAccount,
  isCentralizedWallet,
  isCommercialRealEstate,
  // isCommodityAssetToken,
  isDebitAccount,
  isDecentralizedWallet,
  isExchangeOptions,
  isForwards,
  isFutures,
  isJewelry,
  isLand,
  isLegalEntity,
  isMetalAccount,
  isMyFund,
  isOverTheCounterOptions,
  isPreciousGemstones,
  isPublicFund,
  isResidentialRealEstate,
  isSecuritiesAndStocks,
} from './guards';

const isDtoId = (data: unknown): data is DtoId => typeof data === 'string';

const flattedFilteredDtoIds = (list: unknown[]): DtoId[] => list.flat().filter(isDtoId);

const getAssetOwnershipDtoIds = (asset: AssetsOwnershipPart) => {
  const coOwners = asset.coOwners
    ?.map((coOwner) => {
      if (coOwner.coOwnerEntityDtoId && coOwner.coOwnerDocument) {
        return coOwner.coOwnerDocument.concat([coOwner.coOwnerEntityDtoId]);
      }

      return [];
    })
    .flat();

  return flattedFilteredDtoIds([
    asset.owner,
    asset.coOwnerEntityDtoId,
    asset.coOwnerDocument,
    asset.ownerDocument,
    coOwners,
  ]);
};

const getAssetDocumentsDtoIds = (asset: AssetDto) => {
  return flattedFilteredDtoIds([
    asset.insuranceDocument,
    //
    asset.accessLimitations,
    asset.assetAcquisitions,
    asset.assetProtections,
    asset.otherDocuments,
    asset.paymentMethods,
    asset.powersOfAttorney,
    asset.proofsOfOwnership,
    asset.sourcesOfAcquisition,
  ]);
};

const getBankCellDtoIds = (asset: BankCellDto) => {
  return flattedFilteredDtoIds([
    asset.bank,
    // asset.documents,
    // asset.depositBoxAgreement,
    // asset.insuranceAgreement,
    // asset.powersOfAttorney,
    // asset.otherDocuments,
  ]);
};

const getBondsDtoIds = (asset: BondsDto) => {
  return flattedFilteredDtoIds([asset.holder, asset.powersOfAttorney, asset.otherDocuments]);
};

const getBrokerageAccountDtoIds = (asset: BrokerageAccountDto) => {
  return flattedFilteredDtoIds([
    asset.holder,
    // asset.reportsAndStatements,
    // asset.powersOfAttorney,
    // asset.documents,
  ]);
};

const getCentralizedWalletDtoIds = (asset: CentralizedWalletDto) => {
  return flattedFilteredDtoIds([asset.powersOfAttorney, asset.otherDocuments]);
};

const getCommercialRealEstateDtoIds = (asset: CommercialRealEstateDto) => {
  return flattedFilteredDtoIds([asset.chooseLandPlot, asset.contacts]);
};

// const getCommodityAssetTokenDtoIds = (asset: CommercialRealEstate) => {
//   return flattedFilteredDtoIds([
//     // asset.powersOfAttorney,
//     // asset.otherDocuments,
//     // asset.exchangePlatformBank,
//   ]);
// };

const getDebitAccountDtoIds = (asset: DebitAccountDto) => {
  return flattedFilteredDtoIds([asset.bank, asset.powersOfAttorney, asset.otherDocuments]);
};

const getDecentralizedWalletDtoIds = (asset: DecentralizedWalletDto) => {
  return flattedFilteredDtoIds([asset.powersOfAttorney, asset.otherDocuments]);
};

const getExchangeOptionsDtoIds = (asset: ExchangeOptionsDto) => {
  return flattedFilteredDtoIds([asset.broker, asset.powersOfAttorney, asset.otherDocuments]);
};

const getForwardsDtoIds = (asset: ForwardsDto) => {
  return flattedFilteredDtoIds([
    asset.depositoryStatements,
    // asset.powersOfAttorney,
    // asset.otherDocuments,
  ]);
};

// eslint-disable-next-line sonarjs/no-identical-functions
const getFuturesDtoIds = (asset: FuturesDto) => {
  return flattedFilteredDtoIds([
    asset.depositoryStatements,
    // asset.powersOfAttorney,
    // asset.otherDocuments,
  ]);
};

const getJewelryDtoIds = (asset: JewelryDto) => {
  return flattedFilteredDtoIds([
    asset.personalInformation,
    asset.insuranceDocument,
    // asset.powersOfAttorney,
    // asset.otherDocuments,
  ]);
};

const getLandDtoIds = (asset: LandDto) => {
  return flattedFilteredDtoIds([
    asset.chooseBuilding,
    asset.contacts,
    // asset.powersOfAttorney,
    // asset.otherDocuments,
  ]);
};

const getLegalEntityDtoIds = (asset: LegalEntityDto) => {
  return flattedFilteredDtoIds([
    asset.myAssets,
    asset.contacts,
    // asset.annualReports,
    asset.assetInfoDocuments,
    // asset.powers,
  ]);
};

const getMetalAccountDtoIds = (asset: MetalAccountDto) => {
  return flattedFilteredDtoIds([asset.holder, asset.powersOfAttorney, asset.otherDocuments]);
};

const getMyFundDtoIds = (asset: MyFundDto) => {
  return flattedFilteredDtoIds([
    asset.fundDocuments,
    asset.annualReports,
    asset.powersOfAttorney,
    asset.otherDocuments,
    asset.manager,
    asset.boardOfDirectors,
    asset.managementCompany,
    asset.fundFounder,
    asset.fundFounderDocument,
    asset.otherOwnerDocuments,
  ]);
};

const getOverTheCounterOptionsDtoIds = (asset: OverTheCounterOptionsDto) => {
  return flattedFilteredDtoIds([asset.powersOfAttorney, asset.otherDocuments]);
};

const getPreciousGemstonesDtoIds = (asset: PreciousGemstonesDto) => {
  return flattedFilteredDtoIds([
    asset.purchaseReceipt,
    asset.internationalCertificate,
    asset.additionalAppraisal,
    asset.insuranceDocument,
    // asset.powersOfAttorney,
    // asset.otherDocuments,
  ]);
};

const getPublicFundDtoIds = (asset: PublicFundDto) => {
  return flattedFilteredDtoIds([
    asset.manager,
    asset.boardOfDirectors,
    asset.managementCompany,
    // asset.powersOfAttorney,
    asset.ownershipProof,
  ]);
};

const getResidentialRealEstateDtoIds = (asset: ResidentialRealEstateDto) => {
  return flattedFilteredDtoIds([
    // asset.powersOfAttorney,
    // asset.otherDocuments,
    asset.chooseLandPlot,
    asset.contacts,
  ]);
};

const getSecuritiesAndStocksDtoIds = (asset: SecuritiesAndStocksDto) => {
  return flattedFilteredDtoIds([
    asset.holder,
    // asset.powers,
    asset.personalIdentifiers,
    asset.assetInfoDocuments,
  ]);
};

export const getAssetRelatedDtoIds = (asset: AssetDto & Record<string, unknown>) => {
  let dtoIds: DtoId[] = [];

  if (isBankCell(asset)) {
    dtoIds = getBankCellDtoIds(asset);
  }
  if (isBond(asset)) {
    dtoIds = getBondsDtoIds(asset);
  }
  if (isBrokerageAccount(asset)) {
    dtoIds = getBrokerageAccountDtoIds(asset);
  }
  if (isCentralizedWallet(asset)) {
    dtoIds = getCentralizedWalletDtoIds(asset);
  }
  if (isCommercialRealEstate(asset)) {
    dtoIds = getCommercialRealEstateDtoIds(asset);
  }
  // if (isCommodityAssetToken(asset)) {
  //   dtoIds = getCommodityAssetTokenDtoIds(asset);
  // }
  if (isDebitAccount(asset)) {
    dtoIds = getDebitAccountDtoIds(asset);
  }
  if (isDecentralizedWallet(asset)) {
    dtoIds = getDecentralizedWalletDtoIds(asset);
  }
  if (isExchangeOptions(asset)) {
    dtoIds = getExchangeOptionsDtoIds(asset);
  }
  if (isForwards(asset)) {
    dtoIds = getForwardsDtoIds(asset);
  }
  if (isFutures(asset)) {
    dtoIds = getFuturesDtoIds(asset);
  }
  if (isJewelry(asset)) {
    dtoIds = getJewelryDtoIds(asset);
  }
  if (isLand(asset)) {
    dtoIds = getLandDtoIds(asset);
  }
  if (isLegalEntity(asset)) {
    dtoIds = getLegalEntityDtoIds(asset);
  }
  if (isMetalAccount(asset)) {
    dtoIds = getMetalAccountDtoIds(asset);
  }
  if (isMyFund(asset)) {
    dtoIds = getMyFundDtoIds(asset);
  }
  if (isOverTheCounterOptions(asset)) {
    dtoIds = getOverTheCounterOptionsDtoIds(asset);
  }
  if (isPreciousGemstones(asset)) {
    dtoIds = getPreciousGemstonesDtoIds(asset);
  }
  if (isPublicFund(asset)) {
    dtoIds = getPublicFundDtoIds(asset);
  }
  if (isResidentialRealEstate(asset)) {
    dtoIds = getResidentialRealEstateDtoIds(asset);
  }
  if (isSecuritiesAndStocks(asset)) {
    dtoIds = getSecuritiesAndStocksDtoIds(asset);
  }

  dtoIds = dtoIds.concat(getAssetOwnershipDtoIds(asset), getAssetDocumentsDtoIds(asset));

  const uniqDtoIds = Array.from(new Set(dtoIds));

  return uniqDtoIds;
};
